/**
 * Buyer-facing payment-error copy, shared by every checkout surface
 * (/checkout card + express + subscription, checkout island, ctMode).
 *
 * Why: a bank decline (decline_code `do_not_honor` etc.) surfaces as Stripe's
 * bare "Your card was declined.", so the buyer has no idea the fix is a quick
 * confirm with their bank, so they bounce (2026-08-11: Truist first-purchase
 * decline, buyer gave up after 2 attempts). We translate the decline FAMILY
 * into actionable guidance and always offer the escape hatch (PayPal / another
 * card). Validation errors (wrong CVC, bad number, expired) keep Stripe's own
 * message, which is already precise.
 *
 * Security: for lost/stolen/fraud-flagged cards we deliberately do NOT reveal
 * the reason (Stripe guidance); the buyer sees the generic decline copy.
 */

// null-tolerant: the Checkout-Sessions flow types its error fields as
// `string | null`, the classic StripeError as `string | undefined`.
export type PaymentErrorLike = {
  code?: string | null;
  decline_code?: string | null;
  message?: string | null;
};

/** Decline codes where naming the reason helps the buyer act. */
const INSUFFICIENT_FUNDS = new Set(["insufficient_funds", "withdrawal_count_limit_exceeded", "card_velocity_exceeded"]);

/** Decline codes whose real reason must NOT be shown to the buyer. */
const DO_NOT_REVEAL = new Set([
  "lost_card",
  "stolen_card",
  "fraudulent",
  "pickup_card",
  "restricted_card",
  "security_violation",
  "merchant_blacklist",
]);

function altMethods(paypalAvailable: boolean): string {
  return paypalAvailable ? "PayPal or another card" : "another card";
}

/**
 * Map a Stripe(-shaped) error to buyer-facing copy. Falls back to the
 * provider message, then to a generic line; never returns an empty string.
 */
export function friendlyPaymentError(
  error: PaymentErrorLike | null | undefined,
  opts?: { paypalAvailable?: boolean },
): string {
  const paypal = opts?.paypalAvailable ?? true;
  const code = error?.code || "";
  const decline = error?.decline_code || "";

  if (code === "expired_card" || decline === "expired_card") {
    return `Your card has expired. Please use ${altMethods(paypal)}.`;
  }

  // Bank declines: the card itself is fine, the issuer said no.
  if (code === "card_declined" || decline !== "") {
    if (INSUFFICIENT_FUNDS.has(decline)) {
      return `Your card was declined due to insufficient funds. Please use ${altMethods(paypal)}.`;
    }
    if (DO_NOT_REVEAL.has(decline)) {
      return `Your card was declined by your bank. Please use ${altMethods(paypal)}.`;
    }
    // do_not_honor, generic_decline, try_again_later, call_issuer, ...: the
    // issuer gave no reason. Most common on a first purchase with a new store;
    // a quick confirm with the bank usually clears it.
    return (
      "Your bank declined this payment. This often happens on a first purchase with a new store: " +
      "approving the charge in your banking app or a quick call to your bank usually fixes it. " +
      `You can also try ${altMethods(paypal)}.`
    );
  }

  return error?.message || "Payment failed. Please try again.";
}
